/**
 * Canonical product analytics event names.
 *
 * - Auth: GA4 recommended names (`login`, `sign_up`) for portability to BigQuery/exports.
 * - Product: [domain]_[object]_[action], snake_case, past tense (common Segment/Amplitude style).
 *
 * Do not put PHI in event names or properties (no patient IDs, MRNs, free-text clinical content).
 */

export const AnalyticsEvent = {
  // —— GA4 cross-platform ——
  LOGIN: "login",
  LOGIN_FAILED: "login_failed",
  SIGN_UP: "sign_up",
  SIGN_UP_FAILED: "sign_up_failed",
  LOGOUT: "logout",

  // —— Product (de-identified context only) ——
  /** Clinical AI: user requested treatment recommendation output */
  CLINICAL_AI_TREATMENT_RECOMMENDATIONS_REQUESTED: "clinical_ai_treatment_recommendations_requested",
  /** Clinical AI: user ran medication interaction check */
  CLINICAL_AI_DRUG_INTERACTIONS_CHECKED: "clinical_ai_drug_interactions_checked",
  /** Order workflow: provider submitted an order form (no patient identifiers in props) */
  CLINICAL_ORDER_FORM_SUBMITTED: "clinical_order_form_submitted",
  /** AI processed a document (e.g. extraction pipeline); no patient identifiers in props */
  CLINICAL_AI_DOCUMENT_PROCESSED: "clinical_ai_document_processed",
  /** AI summary read path: a patient/encounter summary was viewed (props carry cache state only) */
  CLINICAL_AI_SUMMARY_VIEWED: "clinical_ai_summary_viewed",
  /** AI summary generation completed (props carry success + durationMs, no patient identifiers) */
  CLINICAL_AI_SUMMARY_GENERATED: "clinical_ai_summary_generated",

  // —— Patients module (never send patient_id, MRN, names, or clinical text) ——
  /** Provider opened the main patients roster */
  PATIENTS_ROSTER_VIEWED: "patients_roster_viewed",
  /** Provider typed a search query in the roster search bar (debounced, non-empty) */
  PATIENTS_SEARCHED: "patients_searched",
  /** Provider cleared all active roster filters */
  PATIENTS_FILTERS_CLEARED: "patients_filters_cleared",
  /** Provider navigated from roster into a patient chart */
  PATIENT_RECORD_OPENED: "patient_record_opened",
  /** Provider landed on the patient chart (detail shell) */
  PATIENT_DETAIL_VIEWED: "patient_detail_viewed",
  /** Provider switched sidebar/tab section on patient chart */
  PATIENT_DETAIL_SECTION_VIEWED: "patient_detail_section_viewed",
  /** Provider exported roster CSV */
  PATIENT_LIST_EXPORTED: "patient_list_exported",
  /** Provider opened invite-to-portal modal from roster */
  PATIENT_INVITE_MODAL_OPENED: "patient_invite_modal_opened",
  /** Portal invite sent successfully */
  PATIENT_INVITE_SENT: "patient_invite_sent",
  /** Patient removed from roster successfully */
  PATIENT_RECORD_DELETED: "patient_record_deleted",
  /** CTA to LyfeAI onboarding / import flow */
  PATIENTS_ONBOARDING_NAV_CLICKED: "patients_onboarding_nav_clicked",
  /** User started EHR sync from patient chart (e.g. Zus) */
  PATIENT_EHR_SYNC_REQUESTED: "patient_ehr_sync_requested",
  /** Sync could not start (e.g. not linked to external id) */
  PATIENT_EHR_SYNC_BLOCKED: "patient_ehr_sync_blocked",
  /** Post-onboarding patient records summary page */
  PATIENT_RECORDS_SUMMARY_VIEWED: "patient_records_summary_viewed",
  /** Tab change on patient records summary */
  PATIENT_RECORDS_TAB_VIEWED: "patient_records_tab_viewed",

  // —— Patient detail — Medications tab ——
  /** Provider initiated sync of selected medications to DrChrono */
  PATIENT_MEDICATIONS_EHR_SYNC_INITIATED: "patient_medications_ehr_sync_initiated",
  /** Provider copied medications list to clipboard */
  PATIENT_MEDICATIONS_COPIED: "patient_medications_copied",
  /** Provider exported medications list as a text file */
  PATIENT_MEDICATIONS_EXPORTED: "patient_medications_exported",

  // —— Patient detail — Conditions tab ——
  /** Provider initiated sync of selected conditions to DrChrono */
  PATIENT_CONDITIONS_EHR_SYNC_INITIATED: "patient_conditions_ehr_sync_initiated",
  /** Provider opened the condition detail sheet */
  PATIENT_CONDITION_DETAIL_VIEWED: "patient_condition_detail_viewed",
  /** Provider triggered AI summary generation for a condition */
  PATIENT_CONDITION_AI_SUMMARY_REQUESTED: "patient_condition_ai_summary_requested",
  /** Provider copied conditions list to clipboard */
  PATIENT_CONDITIONS_COPIED: "patient_conditions_copied",
  /** Provider exported conditions list as a text file */
  PATIENT_CONDITIONS_EXPORTED: "patient_conditions_exported",

  // —— Patient detail — Allergies tab ——
  /** Provider initiated sync of selected allergies to DrChrono */
  PATIENT_ALLERGIES_EHR_SYNC_INITIATED: "patient_allergies_ehr_sync_initiated",
  /** Provider copied allergies list to clipboard */
  PATIENT_ALLERGIES_COPIED: "patient_allergies_copied",
  /** Provider exported allergies list as a text file */
  PATIENT_ALLERGIES_EXPORTED: "patient_allergies_exported",

  // —— Patient detail — Immunizations tab ——
  /** Provider copied immunizations list to clipboard */
  PATIENT_IMMUNIZATIONS_COPIED: "patient_immunizations_copied",
  /** Provider exported immunizations list as a text file */
  PATIENT_IMMUNIZATIONS_EXPORTED: "patient_immunizations_exported",

  // —— Patient detail — Vitals tab ——
  /** Provider copied vitals to clipboard */
  PATIENT_VITALS_COPIED: "patient_vitals_copied",
  /** Provider exported vitals as a text file */
  PATIENT_VITALS_EXPORTED: "patient_vitals_exported",

  // —— Patient detail — Documents tab ——
  /** Provider opened a document preview modal */
  PATIENT_DOCUMENT_VIEWED: "patient_document_viewed",
  /** Provider downloaded a document from the preview modal */
  PATIENT_DOCUMENT_DOWNLOADED: "patient_document_downloaded",
  /** Provider copied document list to clipboard */
  PATIENT_DOCUMENTS_COPIED: "patient_documents_copied",
  /** Provider exported document list as a text file */
  PATIENT_DOCUMENTS_EXPORTED: "patient_documents_exported",

  // —— Patient detail — Clinical Notes tab ——
  /** Provider searched within the clinical notes list (debounced, non-empty) */
  PATIENT_CLINICAL_NOTES_SEARCHED: "patient_clinical_notes_searched",
  /** Provider opened a clinical note PDF/XML preview */
  PATIENT_CLINICAL_NOTE_VIEWED: "patient_clinical_note_viewed",
  /** Provider expanded a clinical note to see full details */
  PATIENT_CLINICAL_NOTE_EXPANDED: "patient_clinical_note_expanded",

  // —— Patient CRUD (server-side mutations) ——
  /** Provider created a new patient record */
  PATIENT_RECORD_CREATED: "patient_record_created",
  /** Provider updated a patient record */
  PATIENT_RECORD_UPDATED: "patient_record_updated",
  /** Admin bulk-deleted multiple patient records */
  PATIENTS_BULK_DELETED: "patients_bulk_deleted",
  /** Provider imported patients from a CSV file */
  PATIENTS_CSV_IMPORTED: "patients_csv_imported",

  // —— EHR sync ——
  /** Patient EHR sync completed (post-outcome; see also PATIENT_EHR_SYNC_REQUESTED for initiation) */
  PATIENT_EHR_SYNCED: "patient_ehr_synced",

  // —— Appointments ——
  /** Provider created a new appointment */
  APPOINTMENT_CREATED: "appointment_created",
  /** Provider cancelled an appointment */
  APPOINTMENT_CANCELLED: "appointment_cancelled",
  /** Provider rescheduled an appointment to a new date/time */
  APPOINTMENT_RESCHEDULED: "appointment_rescheduled",
  /** Provider marked a patient as no-show for an appointment */
  APPOINTMENT_NO_SHOW_MARKED: "appointment_no_show_marked",

  // —— Clinical notes ——
  /** Provider created a clinical or administrative note */
  CLINICAL_NOTE_CREATED: "clinical_note_created",

  // —— Org membership ——
  /** Admin invited a user to join an organization */
  ORG_MEMBER_INVITED: "org_member_invited",
  /** User accepted a pending organization invitation */
  ORG_INVITATION_ACCEPTED: "org_invitation_accepted",
  /** User declined a pending organization invitation */
  ORG_INVITATION_DECLINED: "org_invitation_declined",
  /** User selected an organization on the org-picker screen */
  ORG_SELECTED: "org_selected",

  // —— Auth flows ——
  /** User submitted the forgot-password form (email sent) */
  AUTH_FORGOT_PASSWORD_SUBMITTED: "auth_forgot_password_submitted",
  /** User successfully set a new password via reset/invite link */
  AUTH_PASSWORD_RESET_COMPLETED: "auth_password_reset_completed",

  // —— Settings ——
  /** Provider re-enabled (or reset) the onboarding guide from Settings */
  SETTINGS_ONBOARDING_ENABLED: "settings_onboarding_enabled",

  // —— Bulk patient import wizard ——
  /** CSV file was parsed and preview is shown (step 0 → 1) */
  PATIENTS_BULK_IMPORT_FILE_UPLOADED: "patients_bulk_import_file_uploaded",
  /** Provider confirmed config and started the actual import (step 2 → 3) */
  PATIENTS_BULK_IMPORT_STARTED: "patients_bulk_import_started",
  /** Import job reached the results screen (step 4) */
  PATIENTS_BULK_IMPORT_COMPLETED: "patients_bulk_import_completed",

  // —— EHR patient import ——
  /** Provider imported a single patient from the EHR integration panel */
  PATIENTS_EHR_IMPORT_COMPLETED: "patients_ehr_import_completed",

  // —— Admin dashboard ——
  /** Admin switched to a different tab on the admin dashboard */
  ADMIN_TAB_VIEWED: "admin_tab_viewed",
  /** Admin opened the Invite User dialog */
  ADMIN_USER_INVITE_CLICKED: "admin_user_invite_clicked",
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];
